import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createFeature, createReducer, on } from '@ngrx/store';
import { McpAccessGrant, McpAccessListQuery } from '@niko-crm/ui';
import { McpAccessActions } from './mcp-access.actions';

export interface McpAccessState extends EntityState<McpAccessGrant> {
  total: number;
  /** Last query sent - every mutation reloads it verbatim (design §4.2). */
  query: McpAccessListQuery;
  loading: boolean;
  creating: boolean;
  /** Id of the row whose action (edit tenants / revoke / reactivate / resend / resync) is in flight. */
  mutatingId: string | null;
  error: string | null;
}

export const mcpAccessAdapter: EntityAdapter<McpAccessGrant> = createEntityAdapter<McpAccessGrant>();

const initialState: McpAccessState = mcpAccessAdapter.getInitialState({
  total: 0,
  query: { page: 1, limit: 20 },
  loading: false,
  creating: false,
  mutatingId: null,
  error: null,
});

/**
 * No mutation patches an entity locally: `sync` and `provisioningStatus` are
 * DERIVED server-side (design §1.3), so a locally patched row would show
 * stale drift. Every mutation success is followed by a reload of the current
 * page (`reloadAfterMutation$` in the effects) - the same rule
 * `impersonation-session.reducer.ts` documents.
 */
export const mcpAccessReducer = createReducer(
  initialState,
  on(McpAccessActions.loadGrants, (state, { query }) => ({
    ...state,
    query,
    loading: true,
    error: null,
  })),
  on(McpAccessActions.loadGrantsSuccess, (state, { result }) =>
    mcpAccessAdapter.setAll(result.data, {
      ...state,
      total: result.total,
      query: { ...state.query, page: result.page, limit: result.limit },
      loading: false,
    }),
  ),
  on(McpAccessActions.loadGrantsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  on(McpAccessActions.createGrant, (state) => ({ ...state, creating: true, error: null })),
  on(McpAccessActions.createGrantSuccess, (state) => ({ ...state, creating: false })),
  on(McpAccessActions.createGrantFailure, (state, { error }) => ({
    ...state,
    creating: false,
    error,
  })),

  on(McpAccessActions.updateTenants, (state, { id }) => ({
    ...state,
    mutatingId: id,
    error: null,
  })),
  on(McpAccessActions.updateTenantsSuccess, (state) => ({ ...state, mutatingId: null })),
  on(McpAccessActions.updateTenantsFailure, (state, { error }) => ({
    ...state,
    mutatingId: null,
    error,
  })),

  on(McpAccessActions.revokeGrant, (state, { id }) => ({ ...state, mutatingId: id, error: null })),
  on(McpAccessActions.revokeGrantSuccess, (state) => ({ ...state, mutatingId: null })),
  on(McpAccessActions.revokeGrantFailure, (state, { error }) => ({
    ...state,
    mutatingId: null,
    error,
  })),

  on(McpAccessActions.reactivateGrant, (state, { id }) => ({
    ...state,
    mutatingId: id,
    error: null,
  })),
  on(McpAccessActions.reactivateGrantSuccess, (state) => ({ ...state, mutatingId: null })),
  on(McpAccessActions.reactivateGrantFailure, (state, { error }) => ({
    ...state,
    mutatingId: null,
    error,
  })),

  on(McpAccessActions.resendInvitation, (state, { id }) => ({
    ...state,
    mutatingId: id,
    error: null,
  })),
  on(McpAccessActions.resendInvitationSuccess, (state) => ({ ...state, mutatingId: null })),
  on(McpAccessActions.resendInvitationFailure, (state, { error }) => ({
    ...state,
    mutatingId: null,
    error,
  })),

  on(McpAccessActions.resyncGrant, (state, { id }) => ({ ...state, mutatingId: id, error: null })),
  on(McpAccessActions.resyncGrantSuccess, (state) => ({ ...state, mutatingId: null })),
  on(McpAccessActions.resyncGrantFailure, (state, { error }) => ({
    ...state,
    mutatingId: null,
    error,
  })),

  on(McpAccessActions.getInvitationLink, (state, { id }) => ({
    ...state,
    mutatingId: id,
    error: null,
  })),
  on(McpAccessActions.getInvitationLinkSuccess, (state) => ({ ...state, mutatingId: null })),
  on(McpAccessActions.getInvitationLinkFailure, (state, { error }) => ({
    ...state,
    mutatingId: null,
    error,
  })),
);

export const mcpAccessFeature = createFeature({
  name: 'adminMcpAccess',
  reducer: mcpAccessReducer,
});
